import { useEffect, useMemo, useState } from 'react'
import type { CTA, QuantidadeMPSection } from '../../types/payload'
import { KpiCard } from '../KpiCard'
import { ChartExportButton } from '../charts/ChartExportButton'
import { safeBarWidthPct } from '../../utils/barWidth'
import styles from './Section.module.css'

interface Props {
  section: QuantidadeMPSection
  ctas: CTA[]
  onCtaClick: (cta: CTA) => void
}

const CANAL_LABEL: Record<string, string> = {
  farma: 'Farma / Farma',
  digital_nativo: 'Digital / Digital Nativo',
  atacados: 'Alimentar / Atacado/Atacarejo',
  varejo: 'Alimentar / Supermercado',
}

const CHANNEL_ORDER = ['farma', 'digital_nativo', 'atacados', 'varejo'] as const
const MAX_VISIBLE_NETWORKS = 15

type SortBy = 'total' | 'mp' | 'percent'

interface ChannelSummary {
  canal: string
  totalSkus: number
  displayedMpSkus: number
  displayedPercent: number
}

function formatInteger(value: number) {
  return Math.max(0, Math.round(value)).toLocaleString('pt-BR')
}

function formatPercent(value: number) {
  return value.toLocaleString('pt-BR', {
    minimumFractionDigits: 1,
    maximumFractionDigits: 1,
  })
}

function countDistinctNetworks(records: QuantidadeMPSection['records']) {
  return new Set(
    records.map(record =>
      record.rede
        .trim()
        .normalize('NFKD')
        .replace(/[^\x00-\x7F]/g, '')
        .replace(/\s+/g, ' ')
        .toUpperCase(),
    ),
  ).size
}

export function QuantidadeMP({ section, ctas, onCtaClick }: Props) {
  const [sortBy, setSortBy] = useState<SortBy>('percent')
  const [activeModalChannel, setActiveModalChannel] = useState<string | null>(null)

  const grouped = useMemo(() => {
    return section.records.reduce<Record<string, QuantidadeMPSection['records']>>((acc, record) => {
      if (!acc[record.canal]) {
        acc[record.canal] = []
      }
      acc[record.canal].push(record)
      return acc
    }, {})
  }, [section.records])

  const channelRecordAggregates = useMemo(() => {
    const aggregates = new Map<string, { mpSkus: number; networkCatalogSkus: number }>()

    for (const record of section.records) {
      const current = aggregates.get(record.canal) ?? { mpSkus: 0, networkCatalogSkus: 0 }
      current.mpSkus += Math.max(0, record.mp.distinct_produtos)
      current.networkCatalogSkus += Math.max(
        0,
        record.mp.distinct_produtos + record.mf.distinct_produtos,
      )
      aggregates.set(record.canal, current)
    }

    return aggregates
  }, [section.records])

  const channelSummaries = useMemo(() => {
    const summaries = new Map<string, ChannelSummary>()

    for (const total of section.channel_totals ?? []) {
      const aggregate = channelRecordAggregates.get(total.canal)
      const totalSkus = Math.max(
        0,
        Math.round(total.distinct_produtos ?? aggregate?.networkCatalogSkus ?? total.rows),
      )
      const displayedMpSkus = Math.max(0, Math.round(aggregate?.mpSkus ?? 0))

      summaries.set(total.canal, {
        canal: total.canal,
        totalSkus,
        displayedMpSkus,
        displayedPercent: totalSkus === 0 ? 0 : (displayedMpSkus / totalSkus) * 100,
      })
    }

    for (const [canal, aggregate] of channelRecordAggregates.entries()) {
      if (summaries.has(canal)) {
        continue
      }

      const totalSkus = Math.max(0, Math.round(aggregate.networkCatalogSkus))
      const displayedMpSkus = Math.max(0, Math.round(aggregate.mpSkus))

      summaries.set(canal, {
        canal,
        totalSkus,
        displayedMpSkus,
        displayedPercent: totalSkus === 0 ? 0 : (displayedMpSkus / totalSkus) * 100,
      })
    }

    const totals = [...summaries.values()]

    return totals.sort((left, right) => {
      const leftIndex = CHANNEL_ORDER.indexOf(left.canal as typeof CHANNEL_ORDER[number])
      const rightIndex = CHANNEL_ORDER.indexOf(right.canal as typeof CHANNEL_ORDER[number])
      return (leftIndex === -1 ? Number.MAX_SAFE_INTEGER : leftIndex)
        - (rightIndex === -1 ? Number.MAX_SAFE_INTEGER : rightIndex)
    })
  }, [channelRecordAggregates, section.channel_totals])

  const channelSummariesByCanal = useMemo(
    () => new Map(channelSummaries.map(summary => [summary.canal, summary])),
    [channelSummaries],
  )

  const groupedEntries = useMemo(() => {
    const ordered = CHANNEL_ORDER
      .filter(canal => grouped[canal])
      .map(canal => [canal, grouped[canal]] as const)

    const extraEntries = Object.entries(grouped).filter(
      ([canal]) => !CHANNEL_ORDER.includes(canal as typeof CHANNEL_ORDER[number]),
    )

    return [...ordered, ...extraEntries]
  }, [grouped])

  const sortedRecordsByCanal = useMemo(() => {
    const getPercentValue = (record: QuantidadeMPSection['records'][number]) => {
      const value = record.share.mp_produtos_pct
      return Number.isFinite(value) ? value : 0
    }

    const getMpValue = (record: QuantidadeMPSection['records'][number]) =>
      Math.max(0, record.mp.distinct_produtos)

    const getTotalValue = (record: QuantidadeMPSection['records'][number]) =>
      Math.max(0, record.mp.distinct_produtos + record.mf.distinct_produtos)

    const compareRecords = (
      left: QuantidadeMPSection['records'][number],
      right: QuantidadeMPSection['records'][number],
    ) => {
      if (sortBy === 'percent') {
        const percentDiff = getPercentValue(right) - getPercentValue(left)

        if (percentDiff !== 0) {
          return percentDiff
        }

        const mpDiff = getMpValue(right) - getMpValue(left)

        if (mpDiff !== 0) {
          return mpDiff
        }
      }

      if (sortBy === 'mp') {
        const mpDiff = getMpValue(right) - getMpValue(left)

        if (mpDiff !== 0) {
          return mpDiff
        }
      }

      if (sortBy === 'total') {
        const totalDiff = getTotalValue(right) - getTotalValue(left)

        if (totalDiff !== 0) {
          return totalDiff
        }
      }

      const totalDiff = getTotalValue(right) - getTotalValue(left)

      if (totalDiff !== 0) {
        return totalDiff
      }

      return left.rede.localeCompare(right.rede, 'pt-BR')
    }

    return new Map(
      groupedEntries.map(([canal, records]) => {
        const sorted = records
          .slice()
          .sort(compareRecords)

        return [canal, sorted] as const
      }),
    )
  }, [groupedEntries, sortBy])

  useEffect(() => {
    if (!activeModalChannel) {
      return
    }

    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        setActiveModalChannel(null)
      }
    }

    window.addEventListener('keydown', handleKeyDown)

    return () => window.removeEventListener('keydown', handleKeyDown)
  }, [activeModalChannel])

  useEffect(() => {
    if (!activeModalChannel) {
      return
    }

    const previousOverflow = document.body.style.overflow
    document.body.style.overflow = 'hidden'

    return () => {
      document.body.style.overflow = previousOverflow
    }
  }, [activeModalChannel])

  const globalTotalSkus = Math.max(
    0,
    Math.round(
      section.totals.distinct_produtos
      ?? channelSummaries.reduce((sum, summary) => sum + summary.totalSkus, 0),
    ),
  )
  const globalDisplayedMpSkus = channelSummaries.reduce(
    (sum, summary) => sum + summary.displayedMpSkus,
    0,
  )
  const globalDisplayedPercent = globalTotalSkus === 0
    ? 0
    : (globalDisplayedMpSkus / globalTotalSkus) * 100

  const activeModalRecords = activeModalChannel
    ? (sortedRecordsByCanal.get(activeModalChannel) ?? [])
    : []
  const activeModalSummary = activeModalChannel
    ? channelSummariesByCanal.get(activeModalChannel)
    : undefined

  return (
    <section className={`card ${styles.section}`}>
      <div className={styles.sectionHeader}>
        <div>
          <span className="microlabel">Quantidade de Marca Própria</span>
          <h2 className={styles.sectionTitle}>Catálogo de marca própria por canal e rede monitorada</h2>
        </div>
        <div className={styles.keyFigures}>
          <span>{formatPercent(globalDisplayedPercent)}% MP</span>
          <span>{formatInteger(globalDisplayedMpSkus)} SKUs MP</span>
        </div>
      </div>

      {channelSummaries.length > 0 && (
        <div className={styles.inlineHeader}>
          <span className="microlabel">Resumo comparativo por canal</span>
          <ChartExportButton
            getContainer={() => document.querySelector<HTMLElement>('[data-chart-container="quantidade-mp-resumo-canais"]')}
            filename="quantidade-mp-resumo-canais.png"
            label="Exportar gráfico em PNG"
          />
        </div>
      )}

      {channelSummaries.length > 0 && (
        <div className={styles.metricGrid} data-chart-container="quantidade-mp-resumo-canais">
          {channelSummaries.map(summary => {
            return (
              <KpiCard
                key={summary.canal}
                label={CANAL_LABEL[summary.canal] ?? summary.canal}
                value={`${formatInteger(summary.totalSkus)} SKUs`}
                meta={`${formatInteger(summary.displayedMpSkus)} SKUs MP • ${formatPercent(summary.displayedPercent)}% MP`}
                indicator={summary.displayedPercent > 0 ? 'positive' : 'default'}
                size="compact"
              />
            )
          })}
        </div>
      )}

      <div className={styles.networkControls}>
        <div>
          <span className="microlabel">Leitura das redes</span>
          <p className={styles.networkControlsText}>
            Ordene todas as redes por volume total de sortimento, SKUs de marca própria ou percentual de MP.
          </p>
        </div>
        <div className={styles.segmentSelector} role="tablist" aria-label="Critério de ordenação das redes">
          <button
            type="button"
            className={`${styles.segmentPill} ${sortBy === 'total' ? styles.segmentPillActive : ''}`}
            aria-pressed={sortBy === 'total'}
            onClick={() => setSortBy('total')}
          >
            Total de SKUs
          </button>
          <button
            type="button"
            className={`${styles.segmentPill} ${sortBy === 'mp' ? styles.segmentPillActive : ''}`}
            aria-pressed={sortBy === 'mp'}
            onClick={() => setSortBy('mp')}
          >
            SKUs MP
          </button>
          <button
            type="button"
            className={`${styles.segmentPill} ${sortBy === 'percent' ? styles.segmentPillActive : ''}`}
            aria-pressed={sortBy === 'percent'}
            onClick={() => setSortBy('percent')}
          >
            % MP
          </button>
        </div>
      </div>

      <div className={styles.channelGrid}>
        {groupedEntries.map(([canal, records]) => {
          const channelSummary = channelSummariesByCanal.get(canal)
          const sortedRecords = sortedRecordsByCanal.get(canal) ?? records
          const visibleRecords = sortedRecords.slice(0, MAX_VISIBLE_NETWORKS)
          const hasHiddenRecords = sortedRecords.length > MAX_VISIBLE_NETWORKS

          return (
            <article key={canal} className={styles.channelCard}>
              <div className={styles.inlineHeader}>
                <div className={styles.channelHeader}>
                  <h3 className={styles.channelTitle}>{CANAL_LABEL[canal] ?? canal}</h3>
                  <span className="microlabel">{countDistinctNetworks(records)} redes</span>
                </div>
                <div className={styles.channelStats}>
                  {channelSummary && (
                    <>
                      <span className={styles.channelStatPill}>
                        Sortimento {formatInteger(channelSummary.totalSkus)} SKUs
                      </span>
                      <span className={styles.channelStatPill}>
                        MP: {formatInteger(channelSummary.displayedMpSkus)} SKUs ({formatPercent(channelSummary.displayedPercent)}%)
                      </span>
                    </>
                  )}
                  <ChartExportButton
                    getContainer={() => document.querySelector<HTMLElement>(`[data-chart-container="quantidade-mp-redes-${canal}"]`)}
                    filename={`quantidade-mp-redes-${canal}.png`}
                    label="Exportar gráfico em PNG"
                  />
                </div>
              </div>

              <div className={styles.barTable} data-chart-container={`quantidade-mp-redes-${canal}`}>
                {visibleRecords.map(record => (
                    <div key={record.rede} className={styles.barRow}>
                      <div>
                        <strong>{record.rede}</strong>
                        <small>
                          Catálogo: {(record.mp.distinct_produtos + record.mf.distinct_produtos).toLocaleString('pt-BR')} SKUs · Marca Própria: {record.mp.distinct_produtos.toLocaleString('pt-BR')} SKUs
                        </small>
                      </div>
                      <div
                        className={styles.barTrack}
                        title={`${record.rede}: ${formatPercent(record.share.mp_produtos_pct * 100)}% MP`}
                      >
                        <div
                          className={styles.barFillPrimary}
                          style={{ width: `${safeBarWidthPct(record.share.mp_produtos_pct * 100)}%` }}
                        />
                      </div>
                      <span className={styles.valueLabel}>
                        {formatPercent(record.share.mp_produtos_pct * 100)}%
                      </span>
                    </div>
                  ))}
              </div>

              {hasHiddenRecords && (
                <div className={styles.channelFooter}>
                  <button
                    type="button"
                    className={styles.viewAllButton}
                    onClick={() => setActiveModalChannel(canal)}
                  >
                    Ver todos
                  </button>
                </div>
              )}
            </article>
          )
        })}
      </div>

      {activeModalChannel && (
        <div
          className={styles.modalOverlay}
          role="presentation"
          onClick={() => setActiveModalChannel(null)}
        >
          <div
            className={styles.modalCard}
            role="dialog"
            aria-modal="true"
            aria-labelledby="quantidade-mp-modal-title"
            onClick={event => event.stopPropagation()}
          >
            <div className={styles.modalHeader}>
              <div>
                <span className="microlabel">Lista completa</span>
                <h3 id="quantidade-mp-modal-title" className={styles.modalTitle}>
                  {CANAL_LABEL[activeModalChannel] ?? activeModalChannel}
                </h3>
                {activeModalSummary && (
                  <p className={styles.modalSubtitle}>
                    {formatInteger(activeModalSummary.totalSkus)} SKUs no canal • {formatInteger(activeModalSummary.displayedMpSkus)} SKUs MP
                  </p>
                )}
              </div>
              <div className={styles.channelStats}>
                <ChartExportButton
                  getContainer={() => document.querySelector<HTMLElement>(`[data-chart-container="quantidade-mp-redes-modal-${activeModalChannel}"]`)}
                  filename={`quantidade-mp-redes-${activeModalChannel}-completo.png`}
                  label="Exportar gráfico em PNG"
                />
                <button
                  type="button"
                  className={styles.modalClose}
                  onClick={() => setActiveModalChannel(null)}
                  aria-label="Fechar modal"
                >
                  Fechar
                </button>
              </div>
            </div>

            <div className={styles.modalBody}>
              <div
                className={styles.barTable}
                data-chart-container={`quantidade-mp-redes-modal-${activeModalChannel}`}
              >
                {activeModalRecords.map(record => (
                  <div key={record.rede} className={styles.barRow}>
                    <div>
                      <strong>{record.rede}</strong>
                      <small>
                        Catálogo: {(record.mp.distinct_produtos + record.mf.distinct_produtos).toLocaleString('pt-BR')} SKUs · Marca Própria: {record.mp.distinct_produtos.toLocaleString('pt-BR')} SKUs
                      </small>
                    </div>
                    <div
                      className={styles.barTrack}
                      title={`${record.rede}: ${formatPercent(record.share.mp_produtos_pct * 100)}% MP`}
                    >
                      <div
                        className={styles.barFillPrimary}
                        style={{ width: `${safeBarWidthPct(record.share.mp_produtos_pct * 100)}%` }}
                      />
                    </div>
                    <span className={styles.valueLabel}>
                      {formatPercent(record.share.mp_produtos_pct * 100)}%
                    </span>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      )}

      {ctas.length > 0 && (
        <div className={styles.ctaRow}>
          {ctas.map(cta => (
            <button key={cta.id} type="button" className="btn-cta" onClick={() => onCtaClick(cta)}>
              {cta.label}
            </button>
          ))}
        </div>
      )}
    </section>
  )
}
